/**
 * System Function Dispatcher
 * Maps system function IDs to provider method calls
 */
import { SystemFunction, SystemFunctionMap, ValueType } from '@emdzej/inpax-core';
import { getLogger } from '@emdzej/bimmerz-logger';
import { formatFsLesenReport } from './format-fs.js';
const log = getLogger('INPAX.dispatcher');
/** Set of async function IDs */
const ASYNC_FUNCTIONS = new Set([
    // Input dialogs
    SystemFunction.inputtext,
    SystemFunction.inputnum,
    SystemFunction.inputhex,
    SystemFunction.inputdigital,
    SystemFunction.input2text,
    SystemFunction.input2hexnum,
    SystemFunction.input2hex,
    SystemFunction.inputint,
    SystemFunction.input2int,
    // Toggle list — INPA's multi-select picker. Async because the UI
    // provider awaits the user's selection before resolving.
    SystemFunction.togglelist,
    // `select` opens the same picker dialog as `togglelist` but commits
    // results to the VM's `ControlSession` instead of writing to an
    // out-string. Async for the same reason.
    SystemFunction.select,
    // Message boxes
    SystemFunction.messagebox,
    SystemFunction.infobox,
    // Simulation
    SystemFunction.simnum,
    SystemFunction.simdigital,
    // EDIABAS
    SystemFunction.INPAapiInit,
    SystemFunction.INPAapiEnd,
    SystemFunction.INPAapiJob,
    SystemFunction.INPAapiFsLesen,
    SystemFunction.INPAapiFsLesen2,
    // INP1
    SystemFunction.INP1apiInit,
]);
/** Internal functions handled by interpreter */
const INTERNAL_FUNCTIONS = new Set([
    // State Machine
    SystemFunction.setstatemachine,
    SystemFunction.setstate,
    SystemFunction.callstatemachine,
    SystemFunction.returnstatemachine,
    // Timer
    SystemFunction.settimer,
    SystemFunction.testtimer,
    // Job Control
    SystemFunction.setjobstatus,
    SystemFunction.exit,
    SystemFunction.exitwindows,
    SystemFunction.scriptselect,
    SystemFunction.scriptchange,
    // `select` lives in ASYNC_FUNCTIONS — see above.
    SystemFunction.deselect,
    SystemFunction.control,
    SystemFunction.start,
    SystemFunction.stop,
    SystemFunction.getapistring,
    // Time
    SystemFunction.delay,
    SystemFunction.getdate,
    SystemFunction.gettime,
    // Conversions
    SystemFunction.realtostring,
    SystemFunction.stringtoreal,
    SystemFunction.inttostring,
    SystemFunction.stringtoint,
    SystemFunction.hexconvert,
    SystemFunction.strcat,
    SystemFunction.strlen,
    SystemFunction.midstr,
    SystemFunction.realtoint,
    SystemFunction.inttoreal,
    SystemFunction.bytetoint,
    SystemFunction.inttolong,
    SystemFunction.longtoreal,
    // Binary
    SystemFunction.GetBinaryDataString,
    // File I/O
    SystemFunction.fileopen,
    SystemFunction.fileclose,
    SystemFunction.filewrite,
    SystemFunction.fileread,
    // String Arrays
    SystemFunction.StrArrayCreate,
    SystemFunction.StrArrayDestroy,
    SystemFunction.StrArrayWrite,
    SystemFunction.StrArrayRead,
    SystemFunction.StrArrayGetElementCount,
    SystemFunction.StrArrayDelete,
    // Structures
    SystemFunction.CreateStructure,
    SystemFunction.SetStructureMode,
    SystemFunction.StructureByte,
    SystemFunction.StructureInt,
    SystemFunction.StructureLong,
    SystemFunction.StructureString,
    // UI - delegated to providers but some may require internal handling
    SystemFunction.setscreen,
    SystemFunction.setmenu,
]);
function parseSignature(signature) {
    const params = [];
    if (signature === '()')
        return { params, isVariadic: false };
    if (signature === '(...)')
        return { params, isVariadic: true };
    const inner = signature.slice(1, -1).trim();
    if (!inner)
        return { params, isVariadic: false };
    const isVariadic = inner.endsWith('...');
    const paramStr = isVariadic ? inner.replace(/,?\s*\.\.\.$/, '') : inner;
    const parts = paramStr.split(',').map(p => p.trim()).filter(p => p);
    for (const part of parts) {
        const match = part.match(/^(in|out|inout):\s*(\w+)\s+(\w+)$/i);
        if (match) {
            params.push({
                direction: match[1],
                type: match[2],
                name: match[3],
            });
        }
    }
    return { params, isVariadic };
}
function getSignature(funcId) {
    const info = SystemFunctionMap.get(funcId);
    if (!info)
        return null;
    return parseSignature(info.signature);
}
function popByType(ctx, typeName) {
    switch (typeName.toLowerCase()) {
        case 'string':
            return ctx.popString();
        case 'real':
            return ctx.popReal();
        case 'bool':
            return ctx.popBool();
        case 'byte':
        case 'int':
        case 'long':
        case 'menu':
        case 'screen':
        case 'statemachine':
        case 'state':
        default:
            return ctx.popInt();
    }
}
function inpaTypeToValueType(typeName) {
    switch (typeName.toLowerCase()) {
        case 'bool':
            return ValueType.Bool;
        case 'byte':
            return ValueType.Byte;
        case 'int':
            return ValueType.Int;
        case 'long':
            return ValueType.Long;
        case 'real':
            return ValueType.Real;
        case 'string':
            return ValueType.String;
        case 'menu':
        case 'screen':
        case 'statemachine':
        case 'state':
            return ValueType.ULong;
        default:
            return ValueType.Int;
    }
}
function collectArguments(funcId, ctx) {
    const sig = getSignature(funcId);
    if (!sig) {
        return { inputs: [], outRefs: [], outParams: [] };
    }
    const inputs = [];
    const outRefs = [];
    const outParams = [];
    const popped = [];
    for (let i = sig.params.length - 1; i >= 0; i--) {
        const param = sig.params[i];
        if (param.direction === 'in') {
            const value = popByType(ctx, param.type);
            popped.unshift({ entry: value, param });
        }
        else {
            const ref = ctx.popRef();
            popped.unshift({ entry: ref, param });
        }
    }
    for (const { entry, param } of popped) {
        if (param.direction === 'in') {
            inputs.push(entry);
        }
        else if (param.direction === 'out') {
            outRefs.push(entry);
            outParams.push(param);
        }
        else if (param.direction === 'inout') {
            const ref = entry;
            let value = ref.value;
            if (ref.refInfo && ctx.getVariable) {
                value = ctx.getVariable(ref.refInfo.scope, ref.refInfo.index).value;
            }
            inputs.push(value);
            outRefs.push(ref);
            outParams.push(param);
        }
    }
    return { inputs, outRefs, outParams };
}
function writeOutParams(outRefs, outParams, returnValue, ctx) {
    if (outRefs.length === 0)
        return;
    let outValues;
    if (returnValue === undefined || returnValue === null) {
        outValues = [];
    }
    else if (Array.isArray(returnValue)) {
        outValues = returnValue;
    }
    else {
        outValues = [returnValue];
    }
    for (let i = 0; i < outRefs.length && i < outValues.length; i++) {
        const ref = outRefs[i];
        const param = outParams[i];
        const value = outValues[i];
        const valueType = inpaTypeToValueType(param.type);
        ctx.setOutParam(ref, { type: valueType, flags: 1, value: value });
    }
}
export class SystemFunctionDispatcher {
    runtime;
    constructor(runtime) {
        this.runtime = runtime;
    }
    isAsync(funcId) {
        return ASYNC_FUNCTIONS.has(funcId);
    }
    isInternal(funcId) {
        return INTERNAL_FUNCTIONS.has(funcId);
    }
    async dispatch(funcId, ctx, vm) {
        const { ui, simulation, ediabas, inp1, print, pem, dtm, external, sps } = this.runtime;
        const collected = collectArguments(funcId, ctx);
        const { inputs } = collected;
        const finalize = async (value) => {
            const resolved = value instanceof Promise ? await value : value;
            writeOutParams(collected.outRefs, collected.outParams, resolved, ctx);
        };
        switch (funcId) {
            // === UI: Menu ===
            case SystemFunction.setmenutitle:
                return finalize(ui.setMenuTitle(inputs[0]));
            case SystemFunction.setmenu:
                return finalize(ui.setMenu(inputs[0]));
            case SystemFunction.setitem:
                return finalize(ui.setItem(inputs[0], inputs[1], inputs[2]));
            case SystemFunction.setitemrepeat:
                return finalize(ui.setItemRepeat(inputs[0], inputs[1]));
            // === UI: Screen ===
            case SystemFunction.settitle:
                return finalize(ui.setTitle(inputs[0]));
            case SystemFunction.setscreen:
                return finalize(ui.setScreen(inputs[0], inputs[1]));
            case SystemFunction.setcolor:
                return finalize(ui.setColor(inputs[0], inputs[1]));
            case SystemFunction.clearrect:
                return finalize(ui.clearRect(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.blankscreen:
                return finalize(ui.blankScreen());
            // === UI: Text Output ===
            case SystemFunction.text:
                return finalize(ui.text(inputs[0], inputs[1], inputs[2]));
            case SystemFunction.textout:
                return finalize(ui.textOut(inputs[0], inputs[1], inputs[2]));
            case SystemFunction.ftextout: {
                // INPA signature: `(text, row, col, fontsize, fontattr)` —
                // 5 args, NO colors. Colors come from the current
                // `setcolor(fg, bg)` state. The previous dispatch read
                // inputs[3]/[4] as fg/bg, which silently consumed the
                // script's fontsize/fontattr and produced wrong tints
                // (e.g. `< F1 > Information` rendered as fg=0 bg=1
                // → "white on black" with the docs palette, when the
                // script actually wanted the current setcolor pair).
                const { fg, bg } = ui.getCurrentColors();
                return finalize(ui.fTextOut(inputs[0], inputs[1], inputs[2], fg, bg, inputs[3], inputs[4]));
            }
            case SystemFunction.ftextclear:
                // Same shape as ftextout: `(text, row, col, size, attr)`.
                // The 4th/5th args are font metrics, not colors.
                return finalize(ui.fTextClear(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4]));
            case SystemFunction.hexdump:
                return finalize(ui.hexDump(inputs[0], inputs[1], inputs[2], inputs[3]));
            // === UI: Data Output ===
            case SystemFunction.digitalout:
                return finalize(ui.digitalOut(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4]));
            case SystemFunction.analogout:
                return finalize(ui.analogOut(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6], inputs[7]));
            case SystemFunction.multianalogout:
                // Signature per ref/Inpa.h:201 — identical to
                // analogout plus a trailing int `mode`. Earlier
                // (incorrect) shape treated this as a row/col
                // header + variadic tail; the actual call layout
                // matches analogout positionally.
                return finalize(ui.multiAnalogOut(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6], inputs[7], inputs[8]));
            // === UI: Input ===
            case SystemFunction.getinputstate:
                return finalize(ui.getInputState());
            case SystemFunction.inputtext:
                return finalize(ui.inputText(inputs[0], inputs[1]));
            case SystemFunction.inputnum:
                return finalize(ui.inputNum(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.inputhex:
                return finalize(ui.inputHex(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.inputdigital:
                return finalize(ui.inputDigital(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.input2text:
                return finalize(ui.input2Text(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.input2hexnum:
                return finalize(ui.input2HexNum(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6], inputs[7]));
            case SystemFunction.input2hex:
                return finalize(ui.input2Hex(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6], inputs[7]));
            case SystemFunction.inputint:
                return finalize(ui.inputInt(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.select: {
                // INPA sig: (in: bool MultipleSelectFlag) — no out-string.
                //
                // INPA's `select` opens the same multi-select picker as
                // `togglelist` (we route through `ui.togglelist`) but
                // commits the picks to the VM's `ControlSession` instead
                // of writing them to a script-visible string. Subsequent
                // cyclic SCREEN refresh iterates `session.activeItems()`
                // to drive whatever STEUERN_* job the SCREEN is wired
                // to.
                //
                // We pass `argNum=true` so the dialog returns
                // space-separated 1-based indices rather than OR'd
                // masks — that lets us decode back to individual items
                // without ambiguity from mask-overlap.
                //
                // Empty result is treated as "user cancelled" — strictly
                // it could also mean "user clicked OK with zero picks",
                // but that's a no-op either way and avoids needing a
                // separate cancel channel.
                //
                // IsEmpty guard: when the active screen has no toggle
                // items registered (no empty LineFuncs), return silently
                // instead of opening an empty picker. This matches the
                // `IsEmpty(+0x1f)` early-return in INPA's `FUN_0041aba9`
                // and avoids a UX regression on orphaned F-keys like
                // `m_main` ITEM 7 (`start; select(true)`) in MS430 /
                // KOMBI / LCM, which previously did nothing.
                const multi = inputs[0];
                const items = vm.getScreenExecutor?.()?.getToggleItems() ?? [];
                if (items.length === 0)
                    return;
                const encoded = await ui.togglelist(multi, true, items);
                const session = vm.getControlSession?.();
                if (session) {
                    if (!encoded) {
                        session.cancel();
                    }
                    else {
                        const picks = encoded.split(/\s+/)
                            .filter(s => s.length > 0)
                            .map(s => parseInt(s, 10) - 1)
                            .filter(i => i >= 0 && i < items.length)
                            .map(i => items[i]);
                        session.applySelection(picks);
                    }
                }
                return;
            }
            case SystemFunction.togglelist: {
                // INPA sig: (in: bool MultipleSelectFlag, in: bool ArgNumFlag,
                //             out: string ApiToggleString)
                //
                // Candidates come from the active SCREEN's "empty"
                // LineFunc sub-blocks — each carries a display name in
                // `arg1` and a 9-byte semicolon-hex bitmask in `arg2`.
                // See `ScreenExecutor.getToggleItems()` for the harvest
                // logic and `docs/ipo-format-versions.md` for the
                // reverse-engineering notes that pinned this down.
                //
                // When no screen executor is running (e.g. before
                // `setscreen` fires, or in unit-test setups) the list
                // is empty — the dialog opens with no rows and the
                // user has to cancel; same behaviour as real INPA when
                // the active screen has no `select` / control items.
                const items = vm.getScreenExecutor?.()?.getToggleItems() ?? [];
                return finalize(ui.togglelist(inputs[0], inputs[1], items));
            }
            case SystemFunction.input2int:
                return finalize(ui.input2Int(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6], inputs[7]));
            // === UI: Message Boxes ===
            case SystemFunction.messagebox:
                return finalize(ui.messageBox(inputs[0], inputs[1]));
            case SystemFunction.infobox:
                return finalize(ui.infoBox(inputs[0], inputs[1]));
            case SystemFunction.userboxopen:
                return finalize(ui.userBoxOpen(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5], inputs[6]));
            case SystemFunction.userboxclose:
                return finalize(ui.userBoxClose(inputs[0]));
            case SystemFunction.userboxftextout:
                return finalize(ui.userBoxFTextOut(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4], inputs[5]));
            case SystemFunction.userboxclear:
                return finalize(ui.userBoxClear(inputs[0]));
            case SystemFunction.userboxsetcolor:
                return finalize(ui.userBoxSetColor(inputs[0], inputs[1], inputs[2]));
            // === Simulation ===
            case SystemFunction.simnum:
                return finalize(simulation.simNum(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.simdigital:
                return finalize(simulation.simDigital(inputs[0], inputs[1], inputs[2], inputs[3]));
            // === EDIABAS ===
            case SystemFunction.INPAapiInit:
                // Drive the host's "ensure the cable is open" flow before
                // letting the provider init the comm session. The host
                // (web `ConfigPanel`, CLI prompt, …) is responsible for
                // showing whatever settings UI it has and resolving the
                // promise once the user is done; we then let
                // `ediabas.init()` either open the link (success) or fail
                // (which surfaces as a `job:error` for the script to
                // see). The split keeps the dispatcher generic — no
                // host-specific UI knowledge here.
                return finalize((async () => {
                    // Loop: ensure connection → try init. On failure
                    // ask the host what to do — retry pulls a fresh
                    // transport via `ensureConnected` and re-tries,
                    // continue lets the script proceed (later jobs
                    // will fail), stop throws so the VM halts.
                    // Subsequent iterations pass the previous error
                    // along so the dialog can show it.
                    while (true) {
                        await ui.ensureConnected();
                        try {
                            await ediabas.init();
                            return;
                        }
                        catch (err) {
                            const message = err instanceof Error ? err.message : String(err);
                            const choice = await ui.confirmConnectError(message);
                            if (choice === "stop")
                                throw err;
                            if (choice === "continue")
                                return;
                            // "retry" — loop
                        }
                    }
                })());
            case SystemFunction.INPAapiEnd:
                return finalize(ediabas.end());
            case SystemFunction.INPAapiJob:
                return finalize(ediabas.job(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.INPAapiResultText:
                return finalize(ediabas.resultText(inputs[0], inputs[1], inputs[2]));
            case SystemFunction.INPAapiResultInt:
                return finalize(ediabas.resultInt(inputs[0], inputs[1]));
            case SystemFunction.INPAapiResultSets:
                return finalize(ediabas.resultSets());
            case SystemFunction.INPAapiResultDigital:
                return finalize(ediabas.resultDigital(inputs[0], inputs[1]));
            case SystemFunction.INPAapiResultAnalog:
                return finalize(ediabas.resultAnalog(inputs[0], inputs[1]));
            case SystemFunction.INPAapiResultBinary:
                return finalize(ediabas.resultBinary(inputs[0], inputs[1]));
            case SystemFunction.INPAapiCheckJobStatus:
                return finalize(ediabas.checkJobStatus(inputs[0]));
            case SystemFunction.INPAapiFsLesen:
                return finalize(this.runFsLesen(ediabas, external, inputs[0], inputs[1], "FsLesen"));
            case SystemFunction.INPAapiFsLesen2:
                return finalize(this.runFsLesen(ediabas, external, inputs[0], inputs[1], "FsLesen2"));
            case SystemFunction.INPAapiFsMode:
                return finalize(ediabas.fsMode(inputs[0], inputs[1], inputs[2], inputs[3], inputs[4]));
            // === INP1 ===
            case SystemFunction.INP1apiInit:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.init());
            case SystemFunction.INP1apiEnd:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.end());
            case SystemFunction.INP1apiJob:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.job(inputs[0], inputs[1], inputs[2], inputs[3]));
            case SystemFunction.INP1apiState:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.state());
            case SystemFunction.INP1apiResultText:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.resultText(inputs[0], inputs[1], inputs[2]));
            case SystemFunction.INP1apiResultInt:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.resultInt(inputs[0], inputs[1]));
            case SystemFunction.INP1apiResultSets:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.resultSets());
            case SystemFunction.INP1apiResultReal:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.resultReal(inputs[0], inputs[1]));
            case SystemFunction.INP1apiResultBinary:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.resultBinary(inputs[0], inputs[1]));
            case SystemFunction.INP1apiErrorCode:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.errorCode());
            case SystemFunction.INP1apiErrorText:
                if (!inp1)
                    throw new Error('INP1 provider not available');
                return finalize(inp1.errorText());
            // === Print ===
            case SystemFunction.printscreen:
                return finalize(print.printScreen());
            case SystemFunction.printfile:
                return finalize(print.printFile(inputs[0], inputs[1], inputs[2], inputs[3]));
            // === PEM ===
            case SystemFunction.PEMInitialisiere:
                return finalize(pem.initialisiere());
            case SystemFunction.PEMProtokollKopf:
                return finalize(pem.protokollKopf());
            case SystemFunction.PEMProtokollZeile:
                return finalize(pem.protokollZeile());
            case SystemFunction.PEMSGZ_Kopfzeile:
                return finalize(pem.sgzKopfzeile());
            case SystemFunction.PEMTrennLinie:
                return finalize(pem.trennLinie());
            case SystemFunction.PEMEndLinie:
                return finalize(pem.endLinie());
            case SystemFunction.PEMLoescheTabZeilenPuffer:
                return finalize(pem.loescheTabZeilenPuffer());
            case SystemFunction.PEMUebertrageTabZeilenPuffer:
                return finalize(pem.uebertrageTabZeilenPuffer());
            case SystemFunction.PEMProtokollAusgabe:
                return finalize(pem.protokollAusgabe());
            case SystemFunction.PEMDruckeEtikett:
                return finalize(pem.druckeEtikett());
            case SystemFunction.PEMPrintFormular:
                return finalize(pem.printFormular());
            case SystemFunction.PEMPrinter_ff:
                return finalize(pem.printerFf());
            case SystemFunction.PEMFree_mem:
                return finalize(pem.freeMem());
            case SystemFunction.PEMLoad_formular:
                return finalize(pem.loadFormular());
            case SystemFunction.PEMDefault_druckfeld:
                return finalize(pem.defaultDruckfeld());
            case SystemFunction.PEMDefault_besetzen:
                return finalize(pem.defaultBesetzen());
            case SystemFunction.PEMForget_formular:
                return finalize(pem.forgetFormular());
            case SystemFunction.PEMWrite_druckfeld:
                return finalize(pem.writeDruckfeld());
            // === DTM ===
            case SystemFunction.DTMFindLogUnit:
                return finalize(dtm.findLogUnit(inputs[0]));
            case SystemFunction.DTMGetSGVar:
                return finalize(dtm.getSGVar(inputs[0]));
            case SystemFunction.DTMGetSGArt:
                return finalize(dtm.getSGArt(inputs[0]));
            case SystemFunction.DTMGetVarWert:
                return finalize(dtm.getVarWert(inputs[0]));
            case SystemFunction.DTMSetupGetVarWert:
                return finalize(dtm.setupGetVarWert(inputs[0]));
            case SystemFunction.DTMSetupGetStartPosition:
                return finalize(dtm.setupGetStartPosition());
            case SystemFunction.DTMSetupGetNextAssoc:
                return finalize(dtm.setupGetNextAssoc());
            case SystemFunction.DTMLogUnitEintragen:
                return finalize(dtm.logUnitEintragen(inputs[0]));
            case SystemFunction.DTMSGEintragen:
                return finalize(dtm.sgEintragen(inputs[0], inputs[1]));
            case SystemFunction.DTMLoescheAuftrag:
                return finalize(dtm.loescheAuftrag());
            case SystemFunction.DTMVariableEintragen:
                return finalize(dtm.variableEintragen(inputs[0], inputs[1]));
            case SystemFunction.DTMVariableLoeschen:
                return finalize(dtm.variableLoeschen(inputs[0]));
            case SystemFunction.DTMLoescheAlleVariablen:
                return finalize(dtm.loescheAlleVariablen());
            case SystemFunction.DTMSetupVariableEintragen:
                return finalize(dtm.setupVariableEintragen(inputs[0], inputs[1]));
            case SystemFunction.DTMSetupVariableLoeschen:
                return finalize(dtm.setupVariableLoeschen(inputs[0]));
            // === External ===
            case SystemFunction.winhelp:
                return finalize(external.winHelp(inputs[0]));
            case SystemFunction.winhelpkey:
                return finalize(external.winHelpKey(inputs[0], inputs[1]));
            case SystemFunction.callwin:
                return finalize(external.callWin(inputs[0]));
            case SystemFunction.viewopen:
                return finalize(external.viewOpen(inputs[0], inputs[1]));
            case SystemFunction.viewclose:
                return finalize(external.viewClose());
            // === SPS ===
            case SystemFunction.SPSInit:
                if (!sps)
                    throw new Error('SPS provider not available');
                return finalize(sps.init());
            case SystemFunction.SPSEnd:
                if (!sps)
                    throw new Error('SPS provider not available');
                return finalize(sps.end());
            case SystemFunction.SPSLeseVonSPS:
                if (!sps)
                    throw new Error('SPS provider not available');
                return finalize(sps.leseVonSPS(...inputs));
            case SystemFunction.SPSSendeAnSPS:
                if (!sps)
                    throw new Error('SPS provider not available');
                return finalize(sps.sendeAnSPS(...inputs));
            case SystemFunction.SPSLeseVakWerte:
                if (!sps)
                    throw new Error('SPS provider not available');
                return finalize(sps.leseVakWerte(...inputs));
            default:
                if (this.isInternal(funcId)) {
                    throw new Error(`Internal function 0x${funcId.toString(16)} should be handled by interpreter`);
                }
                throw new Error(`Unknown system function: 0x${funcId.toString(16)}`);
        }
    }
    /**
     * Run an EDIABAS fault-storage job and write a canonical INPA-style
     * report to the file the script passed in. Mirrors what INPA.exe's
     * `INPAapiFsLesen` (FUN_00408ab8) / `INPAapiFsLesen2` (FUN_0040a275)
     * do end-to-end: run job → format result sets per `INPAapiFsMode`
     * bits → write to disk. The host's external provider provides the
     * `writeFile` sink (real fs on Node, in-memory map in the browser).
     *
     * The `viewopen` call the script makes immediately after FsLesen
     * then reads that same file back through the external provider —
     * no `fs:complete` listener glue required.
     */
    async runFsLesen(ediabas, external, ecu, fileName, variant) {
        log.debug({ ecu, fileName, variant }, 'runFsLesen entry');
        if (variant === "FsLesen2") {
            await ediabas.fsLesen2(ecu, fileName);
        }
        else {
            await ediabas.fsLesen(ecu, fileName);
        }
        log.debug({ variant }, 'runFsLesen — ediabas call done, formatting');
        const cfg = ediabas.getFsModeConfig();
        const body = formatFsLesenReport({
            resultSets: () => ediabas.resultSets(),
            resultInt: (name, set) => ediabas.resultInt(name, set),
            resultText: (name, set, fmt) => ediabas.resultText(name, set, fmt),
            resultBinary: (name, set) => ediabas.resultBinary(name, set),
            hasResult: (name, set) => ediabas.hasResult(name, set),
        }, { mode: cfg.mode, variant, ecu });
        // The script's `INPAapiFsMode(mode, fileMode, preInfoFile,
        // postInfoFile, jobName)` lets the SGBD prepend/append static
        // text files around the fault block. We don't ship a way to
        // read those (different file abstraction), so for now we just
        // emit the formatted fault list — matches what real INPA does
        // when no pre/post files are configured.
        await external.writeFile(fileName, body);
        log.debug({ fileName, chars: body.length }, 'runFsLesen done');
    }
}
//# sourceMappingURL=dispatcher.js.map